import styled, { css } from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../formInputsV2/styles/tokens';

// v2 look for the payment screen (WaitingForPaymentStatusV2). Same export names
// as WaitingForPaymentStatus.styles.js on purpose: the component keeps its
// markup, and the look comes from these. The old file stays for the products
// that have not been migrated yet.

const card = css`
  height: 100%;
  padding: 18px 20px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.border};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
  font-family: ${v2FontFamily};
  color: ${v2Colors.text};

  h3 {
    margin: 0 0 14px;
    font-family: ${v2FontFamily};
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
    color: ${v2Colors.text};
  }

  h3 b {
    font-weight: 700;
    color: ${v2Colors.primary};
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;
  }
`;

// "Paga con carta" / "Paga con bonifico": the two main actions, so their titles
// are bigger than the other card titles.
const payTitle = css`
  .pay-title {
    margin-bottom: 18px;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;

    @media (max-width: ${v2Breakpoints.tablet}) {
      font-size: 19px;
    }
  }
`;

// Hero of the screen: the amount to pay, big, with the payment frequency beside it.
export const PaymentFrequencyHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  flex-wrap: wrap;
  margin: 18px 0 16px;
  padding: 18px 22px;
  background: ${v2Colors.primarySoft};
  border: 1px solid ${v2Colors.primaryBorder};
  border-radius: 16px;
  font-family: ${v2FontFamily};

  span {
    display: block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${v2Colors.primaryHover};
  }

  strong {
    display: block;
    margin-top: 2px;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.15;
    color: ${v2Colors.text};
  }

  p {
    margin: 0;
    padding: 6px 14px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid ${v2Colors.primaryBorder};
    font-size: 14px;
    line-height: 1.4;
    color: ${v2Colors.textMuted};
  }

  p b {
    font-weight: 600;
    color: ${v2Colors.primary};
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;

    strong {
      font-size: 27px;
    }
  }
`;

// ---- payment method chooser (carta / bonifico) --------------------------------

export const PaymentMethodSection = styled.div`
  font-family: ${v2FontFamily};

  > h3 {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 700;
    color: ${v2Colors.text};
  }

  > div:not(:first-of-type) {
    margin-top: 12px;
  }
`;

export const PaymentMethodOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const PaymentMethodOption = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border: 1.5px solid ${(props) => (props.$active ? v2Colors.primary : v2Colors.border)};
  border-radius: 14px;
  background: ${(props) => (props.$active ? v2Colors.primarySoft : v2Colors.inputBackground)};
  box-shadow: ${(props) => (props.$active ? `0 0 0 3px ${v2Colors.primarySoftHover}` : 'none')};
  font-family: ${v2FontFamily};
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;

  svg {
    flex-shrink: 0;
    font-size: 22px;
    color: ${(props) => (props.$active ? v2Colors.primary : v2Colors.textMuted)};
  }

  span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  b {
    font-size: 16px;
    font-weight: 700;
    color: ${v2Colors.text};
  }

  small {
    font-size: 12.5px;
    color: ${v2Colors.textMuted};
  }

  &:hover {
    border-color: ${v2Colors.primary};
  }
`;

// ---- customer info card ----------------------------------------------------

export const WaitingForPaymentInfo = styled.div`
  ${card}

  .btn {
    flex-shrink: 0;
    padding: 7px 14px;
    border: 1.5px solid ${v2Colors.primaryBorder};
    border-radius: 10px;
    background: #fff;
    font-family: ${v2FontFamily};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    color: ${v2Colors.primary};
  }

  .btn:hover,
  .btn:focus-visible {
    border-color: ${v2Colors.primary};
    background: ${v2Colors.primarySoft};
    color: ${v2Colors.primary};
  }
`;

export const InfoBoxContainer = styled.div`
  width: 100%;
  margin-top: 0;
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid ${v2Colors.disabledSurface};

  &:last-child {
    border-bottom: none;
  }

  h4 {
    margin: 0;
    flex-shrink: 0;
    font-family: ${v2FontFamily};
    font-size: 13px;
    font-weight: 500;
    color: ${v2Colors.textMuted};
  }

  p {
    margin: 0;
    font-family: ${v2FontFamily};
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    text-align: right;
    color: ${v2Colors.text};
    overflow-wrap: anywhere;
  }
`;

// ---- payment details table ---------------------------------------------------

export const WaitingForPaymentQuoteInfo = styled.div`
  ${card}

  h3.ps-3 {
    padding-left: 0 !important;
    margin-bottom: 14px !important;
  }
`;

export const QuoteInfoContainer = styled.div``;

export const QuoteAmountTable = styled.div`
  .table {
    width: 100%;
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;
    --bs-table-bg: transparent;
  }

  .table > :not(caption) > * > * {
    background-color: transparent;
    box-shadow: none;
  }

  .table thead th {
    padding: 9px 10px;
    border-bottom: 1px solid #d0d5dd;
    background: transparent;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: left;
    text-transform: uppercase;
    color: ${v2Colors.textMuted};
    white-space: nowrap;
  }

  .table tbody th {
    padding: 10px;
    border-bottom: 1px solid #eaecf0;
    font-weight: 500;
    text-align: left;
    color: ${v2Colors.text};
  }

  .table tbody td {
    padding: 10px;
    border-bottom: 1px solid #eaecf0;
    font-weight: 600;
    color: ${v2Colors.text};
  }

  .table thead th:last-child,
  .table tbody td:last-child,
  .table tfoot td:last-child {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .table tfoot th,
  .table tfoot td {
    padding: 12px 10px;
    border-top: 1px solid #d0d5dd;
    border-bottom: none;
    font-weight: 700;
    color: ${v2Colors.text};
  }

  .table tfoot tr:last-child td:last-child {
    font-size: 18px;
    color: ${v2Colors.primary};
  }

  .table tfoot tr:last-child th {
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${v2Colors.textMuted};
  }
`;

// ---- payment status + card payment ----------------------------------------------

export const PayByCardContainer = styled.div`
  ${card}
  ${payTitle}

  .payment-record {
    margin-bottom: 10px;
    padding: 6px 14px;
    max-width: 600px;
    border: 1px solid ${v2Colors.disabledBorder};
    border-radius: 12px;
    background: ${v2Colors.surface};
  }

  .field-row {
    display: flex;
    flex-wrap: wrap;
    padding: 8px 0;
    border-bottom: 1px solid ${v2Colors.disabledSurface};
    font-size: 14px;
  }

  .field-row:last-child {
    border-bottom: none;
  }

  .field-name {
    flex: 1;
    color: ${v2Colors.textMuted};
  }

  .field-name b {
    font-weight: 500;
  }

  .field-value {
    flex: 1;
    font-weight: 600;
  }

  .field-value.pull-right {
    text-align: right;
  }

  .badge {
    padding: 4px 10px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
  }

  .badge.bg-success {
    background: ${v2Colors.primarySoft} !important;
    border-color: ${v2Colors.primaryBorder};
    color: ${v2Colors.primaryHover} !important;
  }

  .badge.bg-warning {
    background: ${v2Colors.warningSoft} !important;
    border-color: ${v2Colors.warningBorder};
    color: ${v2Colors.warningText} !important;
  }

  .badge.bg-danger {
    background: ${v2Colors.dangerSoft} !important;
    border-color: ${v2Colors.dangerBorder};
    color: ${v2Colors.danger} !important;
  }

  @media (max-width: 600px) {
    .field-row {
      flex-direction: column;
      gap: 2px;
    }
  }
`;

export const FormInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  margin-bottom: 4px;

  label {
    margin: 0 0 5px;
    font-family: ${v2FontFamily};
    font-size: 13px;
    font-weight: 500;
    color: ${v2Colors.textMuted};
  }

  input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${(props) => (props.$isInvalid === true ? v2Colors.danger : v2Colors.border)};
    border-radius: 10px;
    background: ${v2Colors.inputBackground};
    font-family: ${v2FontFamily};
    font-size: 15px;
    font-weight: 600;
    color: ${v2Colors.text};

    &::placeholder {
      font-weight: 400;
      color: ${v2Colors.textFaint};
    }

    &:focus,
    &:active,
    &:focus-visible {
      outline: none;
      border-color: ${(props) => (props.$isInvalid === true ? v2Colors.danger : v2Colors.primary)};
      box-shadow: 0 0 0 3px ${(props) => (props.$isInvalid === true ? v2Colors.dangerSoft : v2Colors.primarySoftHover)};
    }
  }
`;

export const FormInputGroupContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;

  input {
    width: 100%;
  }
`;

export const ErrorMessage = styled.p`
  margin: 4px 0 0;
  font-family: ${v2FontFamily};
  font-size: 12px;
  font-weight: 500;
  color: ${v2Colors.danger};
`;

export const WarningInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px 0 0;
  padding: 10px 12px;
  border: 1px solid ${v2Colors.warningBorder};
  border-radius: 10px;
  background: ${v2Colors.warningSoft};

  p {
    margin: 0;
    font-family: ${v2FontFamily};
    font-size: 13px;
    line-height: 1.5;
    color: ${v2Colors.warningText};
  }

  img {
    flex-shrink: 0;
    width: 18px;
    margin-top: 2px;
  }
`;

export const PayNowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  margin-top: 14px;
  padding: 11px 20px;
  border: none;
  border-radius: 12px;
  background: ${v2Colors.primary};
  font-family: ${v2FontFamily};
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  text-align: center;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover,
  &:focus-visible {
    background: ${v2Colors.primaryHover};
  }
`;

// ---- bank transfer ---------------------------------------------------------------

export const PayByBankTransfer = styled.div`
  ${card}
  ${payTitle}

  .footer-button {
    margin: 12px 0 0;
  }

  .footer-button .btn {
    width: 100%;
    padding: 10px 16px;
    border-radius: 10px;
    font-family: ${v2FontFamily};
    font-size: 14px;
    font-weight: 600;
  }

  .footer-button .btn.btn-outline-success {
    border: 1.5px solid ${v2Colors.primaryBorder};
    background: #fff;
    color: ${v2Colors.primary};
  }

  .footer-button .btn.btn-outline-success:hover {
    border-color: ${v2Colors.primary};
    background: ${v2Colors.primarySoft};
  }

  .footer-button .btn.btn-success {
    border: none;
    background: ${v2Colors.primary};
    color: #fff;
  }

  .footer-button .btn.btn-success:hover {
    background: ${v2Colors.primaryHover};
  }
`;

// "Incasso" (compensation) form: payment-method select + submit, on one row.
export const CompensationForm = styled.form`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;

  > div:first-child {
    flex: 1 1 auto;
    min-width: 0;
    margin-bottom: 0;
  }

  ${PayNowButton} {
    flex: 0 0 auto;
    width: auto;
    min-width: 140px;
    margin-top: 26px;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;

    ${PayNowButton} {
      width: 100%;
      margin-top: 4px;
    }
  }
`;

export const BankTransferInfo = styled.ul`
  margin: 0 0 14px;
  padding: 12px 14px;
  list-style: none;
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 12px;
  background: ${v2Colors.surface};
`;

export const BankTransferInfoItem = styled.li`
  margin: 0 0 6px;
  font-family: ${v2FontFamily};
  font-size: 13px;
  font-weight: 500;
  color: ${v2Colors.textMuted};

  &:last-child {
    margin-bottom: 0;
  }

  span {
    margin-left: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${v2Colors.text};
    overflow-wrap: anywhere;
  }
`;

const proofAccent = (valid) => {
  if (valid === 1) {
    return v2Colors.primary;
  }
  if (valid === 0) {
    return v2Colors.danger;
  }
  return v2Colors.warning;
};

// `$valid` mirrors the proof state (1 valid, 0 not valid, null being checked)
// and only drives the accent on the left edge.
export const PaymentProofStyled = styled.div`
  margin: 14px 0;
  padding: 12px 14px;
  border: 1px solid ${v2Colors.disabledBorder};
  border-left: 4px solid ${(props) => (props.$valid === undefined ? v2Colors.disabledBorder : proofAccent(props.$valid))};
  border-radius: 12px;

  h6 {
    margin: 0 0 8px;
    font-family: ${v2FontFamily};
    font-size: 14px;
    font-weight: 600;
    color: ${v2Colors.text};
  }

  .alert {
    margin: 8px 0 0;
    padding: 8px 12px;
    border: 1px solid transparent;
    border-radius: 10px;
    font-family: ${v2FontFamily};
    font-size: 13px;
  }

  .alert.alert-success {
    background: ${v2Colors.primarySoft};
    border-color: ${v2Colors.primaryBorder};
    color: #05603a;
  }

  .alert.alert-danger {
    background: ${v2Colors.dangerSoft};
    border-color: ${v2Colors.dangerBorder};
    color: ${v2Colors.danger};
  }

  .alert.alert-warning {
    background: ${v2Colors.warningSoft};
    border-color: ${v2Colors.warningBorder};
    color: ${v2Colors.warningText};
  }

  /* The uploaded proof is only a preview: keep it short (the markup sets a
     250px min-height inline). */
  embed {
    display: block;
    width: 100% !important;
    height: 300px !important;
    min-height: 0 !important;
    margin-top: 10px !important;
    border: 1px solid ${v2Colors.disabledBorder};
    border-radius: 8px;
  }

  /* Image proofs take half of the card's width, centered, and keep their
     proportions; a tall image scrolls inside the frame. */
  .proof-image {
    width: 50%;
    max-height: 350px;
    margin: 10px auto 0;
    overflow-y: auto;
    border: 1px solid ${v2Colors.disabledBorder};
    border-radius: 8px;
    background: ${v2Colors.surface};
  }

  .proof-image img {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    .proof-image {
      width: 100%;
    }
  }
`;
